import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { User } from "../types/user.type";
import axios from "axios";
import { api } from "../api/api";

interface AuthState {
  token: string | null;
  authenticated: boolean | null;
  user: User | null;
}

interface AuthContext {
  authState: AuthState;
  setAuthState: React.Dispatch<React.SetStateAction<AuthState>>;
  onLogin: (username: string, password: string) => Promise<any>;
  onLogout: () => void;
}

const defaultAuthContext: AuthContext = {
  authState: {
    token: null,
    authenticated: null,
    user: null,
  },
  setAuthState: () => { },
  onLogin: async () => Promise.resolve(),
  onLogout: async () => Promise.resolve(),
};

const AuthContext = createContext(defaultAuthContext);

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const getInitialAuthState = (): AuthState => {
    const savedAuthState = localStorage.getItem("authState");
    return savedAuthState
      ? JSON.parse(savedAuthState)
      : { token: null, authenticated: null, user: null };
  };

  const [authState, setAuthState] = useState<AuthState>(getInitialAuthState());

  const fetchUserData = async (token: string) => {
    try {
      const response = await api.get(`/user/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const user = response.data.user;
      setAuthState((prev) => ({
        ...prev,
        user,
        authenticated: true,
      }));
    } catch (error: any) {
      console.error("Ошибка при получении данных пользователя:", error);
      setAuthState({
        token: null,
        authenticated: false,
        user: null,
      });
      localStorage.removeItem("token");
      localStorage.removeItem("authState");
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      fetchUserData(token);
    }
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await api.post(`/user/login`, {
        username,
        password,
      });
      const { token, user } = response.data;

      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      const newAuthState: AuthState = {
        token,
        authenticated: true,
        user,
      };

      setAuthState(newAuthState);
      localStorage.setItem("token", token);
      localStorage.setItem("authState", JSON.stringify(newAuthState));
      return response.data;
    } catch (error: any) {
      console.error("Ошибка при входе:", error.response);
      return error.response;
    }
  };

  const logout = async () => {
    localStorage.clear();
    setAuthState({
      token: null,
      authenticated: false,
      user: null,
    });
    delete axios.defaults.headers.common["Authorization"];
  };

  return (
    <AuthContext.Provider
      value={{
        authState,
        onLogin: login,
        onLogout: logout,
        setAuthState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth должен быть использован внутри AuthProvider");
  }
  return context;
};
